import { Fragment, SetStateAction, Suspense, useEffect, useState } from 'react'

import { lazily } from 'react-lazily'

import { useAppDispatch, useAppSelector } from '@App/hooks'
import { MODAL_TYPES } from '@App/redux/slices/modals/enums'
import { closeModal } from '@App/redux/slices/modals/ModalSlice'

import TermAndConditionsModal from '@UI/crypto_exchange/desktop/components/modals/TermAndConditionsModal'

import { LoaderBlocks } from '../../common/Loader/components/LoaderBlocks'
import { AccountCreatedModal } from '../AccountCreatedModal'
import { ChooseAccountCurrencyModal } from '../ChooseAccountCurrencyModal'
import { EditAccountNameModal } from '../EditAccountNameModal'
import { SendReportModal } from '../SendReportModal'
import { SwitchAccountModal } from '../SwitchAccountModal'
import { ProblemGamblerModal } from '@UI/crypto_exchange/desktop/components/modals'

const {
  BorrowRepayModal,
  EmailChangeVerificationModal,
  MarginTransferModal,
  SelectCurrencyModal,
  ChangeNicknameModal,
  DeleteTrustDeviceModal,
  RequestApprovedModal,
  FundAccountModal,
  ConfirmModal,
  SimpleEarnModal,
  OfferConfirmModal,
  OfferCreatedModal,
  SelectP2PPayMethodModal,
  SelectP2PAccountsModal,
  AddP2PPayAccountModal,
  PaymentConfirmationModal,
  NotificationInfoModal,
  LoanBorrowModal,
  RenewalModal,
  LoanRepayModal,
  LoanAdjustLTVModal,
  LoginModal,
  CloseAccountModal,
  ForgotPasswordModal,
  RegistrationModal,
  ForgotUsernameModal,
  CloseAccountMultipleModal,
  CheckMonthStatsAfterLoginModal,
  ExcludeKYCModal,
  FreezePeriodModal,
  InactivityNoticeModal,
  UpdateTermsAndConditionsModal,
  SessionTimeoutModal,
  JurisdictionModal,
  RealityCheckPeriodModal,
  TraidingOptionsModal,
  PersonalLimitModal,
  UserSettingsModal,
  MarketsListModal,
  RealityChecksModal,
  PlaySafeSessionTimeoutModal,
  SelfExclusionLimitModal,
  UpdateDocumentsModal
} = lazily(() => import('@UI/crypto_exchange/desktop/components/modals'))

export default function ModalContainer() {
  const dispatch = useAppDispatch()

  const { current } = useAppSelector((state) => state.modals)
  const isOpen = !!current

  const [scrollBarWidth, setScrollBarWidth] = useState<SetStateAction<number | null>>(null)

  const handleCloseModal = () => dispatch(closeModal())

  const showScroll = () => {
    document.body.classList.remove('overflow')
    document.body.style.paddingRight = ''
  }

  const hideScroll = () => {
    document.body.classList.add('overflow')
    document.body.style.paddingRight = `${scrollBarWidth}px`
  }

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => hideScroll(), 10)
    } else {
      setTimeout(() => showScroll(), 10)
    }
  }, [isOpen])

  let modal = null

  if (current) {
    switch (current.modalType) {
      case MODAL_TYPES.BORROW_REPAY:
        modal = <BorrowRepayModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.PLAY_SAFE_DEPOSIT_LIMIT_MODAL:
      case MODAL_TYPES.PLAY_SAFE_BET_LIMIT_MODAL:
      case MODAL_TYPES.PLAY_SAFE_LOSS_LIMIT_MODAL:
        modal = <PersonalLimitModal isOpen={isOpen} setIsOpen={handleCloseModal} popupData={current.data} />
        break
      case MODAL_TYPES.PLAY_SAFE_REALITY_CHECK_MODAL:
        modal = <RealityChecksModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.PROBLEM_GAMBLER_MODAL:
        modal = <ProblemGamblerModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.PLAY_SAFE_SESSION_TIMEOUT_MODAL:
        modal = <PlaySafeSessionTimeoutModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.PLAY_SAFE_SELF_EXCLUSION_MODAL:
        modal = <SelfExclusionLimitModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.JURISDICTION_MODAL:
        modal = <JurisdictionModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.REALITY_CHECK_PERIOD_MODAL:
        modal = <RealityCheckPeriodModal isOpen={isOpen} setIsOpen={handleCloseModal} />
        break
      case MODAL_TYPES.SESSION_TIMEOUT_MODAL:
        modal = <SessionTimeoutModal isOpen={isOpen} setIsOpen={handleCloseModal} popupData={current.data} />
        break
      case MODAL_TYPES.UPDATE_TERMS_AND_CONDITIONS_MODAL:
        modal = <UpdateTermsAndConditionsModal isOpen={isOpen} setIsOpen={handleCloseModal} popupData={current.data} />
        break
      case MODAL_TYPES.FREEZE_PERIOD_MODAL:
        modal = <FreezePeriodModal isOpen={isOpen} setIsOpen={handleCloseModal} />
        break
      case MODAL_TYPES.INACTIVITY_NOTICE_MODAL:
        modal = <InactivityNoticeModal isOpen={isOpen} setIsOpen={handleCloseModal} />
        break
      case MODAL_TYPES.CLOSE_ACCOUNT_MODAL:
        modal = <CloseAccountModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.CLOSE_ACCOUNT_MULTIPLE_MODAL:
        modal = <CloseAccountMultipleModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.CHANGE_NICKNAME:
        modal = <ChangeNicknameModal isOpen={isOpen} setIsOpen={handleCloseModal} />
        break
      case MODAL_TYPES.LOGIN_MODAL:
        modal = <LoginModal isOpen={isOpen} setIsOpen={handleCloseModal} />
        break
      case MODAL_TYPES.REGISTRATION_MODAL:
        modal = <RegistrationModal isOpen={isOpen} setIsOpen={handleCloseModal} />
        break
      case MODAL_TYPES.FORGOT_USERNAME_MODAL:
        modal = <ForgotUsernameModal isOpen={isOpen} setIsOpen={handleCloseModal} />
        break
      case MODAL_TYPES.FORGOT_PASSWORD_MODAL:
        modal = <ForgotPasswordModal isOpen={isOpen} setIsOpen={handleCloseModal} />
        break
      case MODAL_TYPES.EXCLUDE_KYC_MODAL:
        modal = <ExcludeKYCModal isOpen={isOpen} setIsOpen={handleCloseModal} />
        break
      case MODAL_TYPES.CHECK_MONTH_STATS_AFTER_LOGIN_MODAL:
        modal = <CheckMonthStatsAfterLoginModal isOpen={isOpen} setIsOpen={handleCloseModal} />
        break
      case MODAL_TYPES.DELETE_TRUST_DEVICE:
        modal = <DeleteTrustDeviceModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.EMAIL_CHANGE_VERIFICATION:
        modal = <EmailChangeVerificationModal isOpen={isOpen} setIsOpen={handleCloseModal} />
        break
      case MODAL_TYPES.MARGIN_TRANSFER:
        modal = <MarginTransferModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.SELECT_CURRENCY:
        modal = <SelectCurrencyModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.REQUEST_APPROVED:
        modal = <RequestApprovedModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.FUND_ACCOUNT:
        modal = <FundAccountModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.CONFIRM_MODAL:
        modal = <ConfirmModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.SIMPLE_EARN_MODAL:
        modal = <SimpleEarnModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.P2P_OFFER_CONFIRM_MODAL:
        modal = <OfferConfirmModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.P2P_OFFER_CREATED_MODAL:
        modal = <OfferCreatedModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.SELECT_P2P_PAY_METHOD:
        modal = <SelectP2PPayMethodModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.SELECT_P2P_ACCOUNT:
        modal = <SelectP2PAccountsModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.ADD_P2P_PAY_ACCOUNT:
        modal = <AddP2PPayAccountModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.P2P_PAYMENT_CONFIRMATION:
        modal = <PaymentConfirmationModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.NOTIFICATION_INFO:
        modal = <NotificationInfoModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.LOAN_BORROW_MODAL:
        modal = <LoanBorrowModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.RENEWAL_MODAL:
        modal = <RenewalModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.LOAN_REPAY_MODAL:
        modal = <LoanRepayModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.LOAN_ADJUST_LTV_MODAL:
        modal = <LoanAdjustLTVModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.USER_SETTINGS_MODAL:
        modal = <UserSettingsModal isOpen={isOpen} setIsOpen={handleCloseModal} />
        break
      case MODAL_TYPES.MARKETS_LIST_MODAL:
        modal = <MarketsListModal isOpen={isOpen} setIsOpen={handleCloseModal}/>
        break
      case MODAL_TYPES.TRAIDING_OPTIONS_MODAL:
        modal = <TraidingOptionsModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break

      case MODAL_TYPES.TERMS_AND_CONDITIONS:
        modal = (
          <TermAndConditionsModal
            isOpen={isOpen}
            setIsOpen={handleCloseModal}
            withOutsideClick={false}
            currentTerm={current.data}
          />
        )
        break
      case MODAL_TYPES.CHOOSE_ACCOUNT_CURRENCY_MODAL:
        modal = <ChooseAccountCurrencyModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.ACCOUNT_CREATED_MODAL:
        modal = <AccountCreatedModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.SEND_REPORT_MODAL:
        modal = <SendReportModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      case MODAL_TYPES.UPDATE_DOCUMENTS_MODAL:
        modal = <UpdateDocumentsModal isOpen={isOpen} setIsOpen={handleCloseModal} {...current.data} />
        break
      default:
        return null
    }
  }

  return (
    <Suspense fallback={<LoaderBlocks />}>{modal && <Fragment key={current?.modalType}>{modal}</Fragment>}</Suspense>
  )
}
